export type SnapshotStabilityPhase = "SETTLING" | "STABLE";

export interface S1SnapshotForStability {
  isComplete?: boolean;
  blockedReasons?: string[];
  capture?: { scope_frozen?: boolean };
  ocr?: { terminal?: boolean };
  signals?: { ready?: boolean };
}

export interface SnapshotStabilityState {
  phase: SnapshotStabilityPhase;
  signature: string;
  lastTransitionAtMs: number;
  stablePollCount: number;
  stableMs: number;
}

export interface SnapshotStabilityOptions {
  minStablePolls: number;
  minStableMs: number;
}

export const DEFAULT_SNAPSHOT_STABILITY_OPTIONS: SnapshotStabilityOptions = {
  minStablePolls: 3,
  minStableMs: 3000,
};

export function createInitialSnapshotStabilityState(nowMs: number = Date.now()): SnapshotStabilityState {
  return {
    phase: "SETTLING",
    signature: "",
    lastTransitionAtMs: nowMs,
    stablePollCount: 0,
    stableMs: 0,
  };
}

function stableBool(value: unknown): boolean {
  return value === true;
}

export function buildS1SnapshotSignature(snapshot: S1SnapshotForStability | null | undefined): string {
  if (!snapshot) return "null";
  const blockedReasons = Array.isArray(snapshot.blockedReasons)
    ? snapshot.blockedReasons.map((value) => String(value ?? "").trim().toUpperCase()).filter(Boolean).sort()
    : [];

  return JSON.stringify({
    isComplete: stableBool(snapshot.isComplete),
    blockedReasons,
    captureScopeFrozen: stableBool(snapshot.capture?.scope_frozen),
    ocrTerminal: stableBool(snapshot.ocr?.terminal),
    signalsReady: stableBool(snapshot.signals?.ready),
  });
}

export function advanceSnapshotStability(
  prev: SnapshotStabilityState,
  snapshot: S1SnapshotForStability | null | undefined,
  nowMs: number = Date.now(),
  options: SnapshotStabilityOptions = DEFAULT_SNAPSHOT_STABILITY_OPTIONS,
): SnapshotStabilityState {
  const signature = buildS1SnapshotSignature(snapshot);

  if (!prev.signature || prev.signature !== signature) {
    return {
      phase: "SETTLING",
      signature,
      lastTransitionAtMs: nowMs,
      stablePollCount: 1,
      stableMs: 0,
    };
  }

  const stablePollCount = prev.stablePollCount + 1;
  const stableMs = Math.max(0, nowMs - prev.lastTransitionAtMs);
  const isStable = stablePollCount >= options.minStablePolls || stableMs >= options.minStableMs;

  return {
    phase: isStable ? "STABLE" : "SETTLING",
    signature,
    lastTransitionAtMs: prev.lastTransitionAtMs,
    stablePollCount,
    stableMs,
  };
}
